<script setup lang="ts">
import { KsDrawer } from '@aschehoug/kloss'
import { faBars } from '@fortawesome/pro-regular-svg-icons'

const open = defineModel('open', {
  default: false,
})

const { theme } = useTheme()

const { loggedIn } = useUserSession()

const links = useLeftNavItems()

const dropdownNavItems = useProfileDropdownItems()

const { size: cartSize } = useCartMetadata()

const showPlatformCta = computed(() => getNavigationContext() === 'marketing' && loggedIn.value)

const belowSeparatorItems = computed<NavItemProps[]>(() => {
  const items: NavItemProps[] = [...dropdownNavItems.value]

  if (getNavigationContext() === 'marketing' && cartSize.value > 0) {
    items.push({
      ...navItems.cart,
      badge: cartSize.value,
    })
  }

  if (getNavigationContext() === 'default') {
    items.push(navItems.workspace)
  }

  return items
})
</script>

<template>
  <KsDrawer title="" :open="open" size="100%" position="top" class="px-s py-5 md:px-5 md:pb-7" :dismissable="false">
    <template #header>
      <div class="flex items-center gap-x-xl justify-between w-full">
        <NavLogo />
        <nav aria-label="Mobilmeny" class="flex-1 items-center flex justify-end">
          <NavToggle
            v-model="open"
            name="Meny"
            :icon="faBars"
            class="!bg-transparent focus:!bg-transparent !outline-transparent"
          />
        </nav>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-xl h-full pt-2xl md:pt-xl">
        <div class="flex flex-col gap-l px-5 md:px-7">
          <ul v-if="links.length > 0" class="flex flex-col items-start grow gap-m rounded-b-4xl">
            <li v-for="item in links" :key="item.label">
              <NuxtLink :to="item.to" class="title-m !font-light no-underline dark:text-carbon-50">
                {{ item.label }}
              </NuxtLink>
            </li>
          </ul>
          <template v-if="loggedIn">
            <div class="h-px flex w-full bg-[--divider] topbar:hidden" />
            <ul class="flex flex-col items-start grow gap-3 rounded-b-4xl pendo-mobile-menu topbar:hidden">
              <li v-for="item in belowSeparatorItems" :key="item.label">
                <NavButton v-bind="item" class="title-s !font-light no-underline !p-2xs" :icon="item.icon" />
              </li>
            </ul>
          </template>
        </div>
        <div class="mt-auto flex flex-col gap-xs w-full [&>*]:w-full">
          <Button v-if="showPlatformCta" v-bind="navItems.overviewExternal" :color="theme" target="_blank" class="topbar:hidden">
            {{ navItems.overviewExternal.label }}
          </Button>
          <Button v-if="!loggedIn" v-bind="navItems.purchaseSubscription" class="w-full topbar:hidden">
            {{ navItems.purchaseSubscription.label }}
          </Button>
          <!-- The bar has no logout, so this stays on for logged-in users while the rest folds away. -->
          <HeaderAuthButton :class="{ 'topbar:hidden': !loggedIn }" />
        </div>
      </div>
    </template>
  </KsDrawer>
</template>

<style scoped lang="postcss">
:deep(.ks-dialog-body) {
  @apply h-[100dvh] max-h-full;
}

:deep(.ks-dialog .ks-dialog-body) {
  @apply !overflow-y-auto overscroll-contain;
}

:deep(.ks-dialog-body),
:deep(.ks-dialog-header) {
  @apply p-0;
}
</style>
